import React, { useState, useEffect } from "react";
import { PLATFORMS, normalizeHandle } from "@/lib/socialHandles";

/**
 * HandleInput
 * ────────────
 * A fixed-prefix social handle field. The user only types the handle portion
 * (e.g. "gilmaakaroun") — the platform URL prefix is displayed as a static
 * visual prefix inside the field. If a full URL is pasted, the handle is
 * extracted automatically. If an "@" is typed as the first character, it is
 * removed.
 *
 * Props:
 *   platform — one of "instagram" | "facebook" | "tiktok" | "youtube"
 *   value    — current handle (bare handle, e.g. "gilmaakaroun")
 *   onChange — called with the new bare handle
 *   testid   — data-testid prefix
 *   label    — visible field label
 *   error    — optional error to render inline
 */
export default function HandleInput({ platform, value = "", onChange, testid, label, error }) {
  const cfg = PLATFORMS[platform];
  const [raw, setRaw] = useState(value || "");
  const [localError, setLocalError] = useState("");

  // Keep external -> local in sync (e.g. after a normalize on blur mutates value)
  useEffect(() => { setRaw(value || ""); }, [value]);

  const commit = (input) => {
    if (!input || !input.trim()) {
      setLocalError("");
      onChange("");
      return "";
    }
    const result = normalizeHandle(platform, input);
    if (!result.ok) {
      setLocalError(result.error || "Invalid handle.");
      onChange("");
      return "";
    }
    setLocalError("");
    onChange(result.handle);
    return result.handle;
  };

  const onBlur = () => {
    const clean = commit(raw);
    if (clean) setRaw(clean);
  };

  const onPaste = (e) => {
    // If the user pastes a full URL, immediately normalize
    const text = e.clipboardData.getData("text");
    if (/^https?:\/\//i.test(text)) {
      e.preventDefault();
      const clean = commit(text);
      setRaw(clean);
    }
  };

  const errText = error || localError;

  return (
    <div>
      {label && <div className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)] mb-1.5">{label}</div>}
      <div
        className={`flex items-stretch rounded-xl border-2 overflow-hidden ${
          errText ? "border-[var(--panini-red)]" : "border-[var(--ink)]"
        }`}
        data-testid={`${testid}-wrap`}
      >
        <span className="px-3 py-2.5 bg-[var(--panini-yellow-soft)]/60 text-[13px] text-[var(--smoke)] font-mono whitespace-nowrap select-none flex items-center">
          {cfg?.label || ""}
        </span>
        <input
          type="text"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onBlur={onBlur}
          onPaste={onPaste}
          className="flex-1 px-2.5 py-2.5 text-sm min-w-0"
          placeholder="yourhandle"
          autoComplete="off"
          spellCheck={false}
          data-testid={testid}
        />
      </div>
      {errText && (
        <div className="mt-1 text-[11px] text-[var(--panini-red)] font-bold">{errText}</div>
      )}
    </div>
  );
}
